import { useMemo } from "react";
import { Bell, Mail, Phone } from "lucide-react";
import { Badge } from "../../primitives/Badge";
import { Button } from "../../primitives/Button";
import { Dropdown } from "../../primitives/Dropdown";
import { IconButton } from "../../primitives/IconButton";
import { Tooltip } from "../../primitives/Tooltip";
import { cn } from "../../utils/cn";

export type ClientRelationship = "active" | "inactive" | "paused";
export type InterestLevel = "unknown" | "low" | "medium" | "high" | "later" | "none";
export type QuoteStatus = "draft" | "sent" | "accepted" | "rejected" | "expired";
export type OperationStatus =
  | "waiting_deposit"
  | "in_production"
  | "ready_to_deliver"
  | "delivering"
  | "done"
  | "blocked";
export type InvoiceState = "planned" | "sent" | "paid" | "overdue";

export type ClientStrategicHeaderData = {
  client: {
    id: string;
    name: string;
    region?: string;
    ambassadorName?: string;
    sinceYear?: number;
    relationship: ClientRelationship;
    xlHubActive?: boolean;
    score10?: number;
    grade?: string;
    alertsCount?: number;
    phone?: string;
    email?: string;
    lastPaidDaysAgo?: number;
    scoreReasons?: string[];
  };
  commercial: {
    interest: InterestLevel;
    quotes: { status: QuoteStatus; count: number }[];
    notes?: string[];
    lastSentAt?: string;
  };
  operations: {
    items: { status: OperationStatus; count: number; nextDueDate?: string }[];
  };
  finance: {
    invoices: { state: InvoiceState; amount: number; count?: number }[];
    depositExpected?: number;
    finalPaymentExpected?: number;
  };
  actionsNeeded?: { count: number; items: { label: string; severity: "info" | "warning" | "critical"; onClick?: () => void }[] };
};

type ClientStrategicHeaderProps = ClientStrategicHeaderData & {
  onNewQuote?: () => void;
  onOpenActions?: () => void;
  onQuickCall?: () => void;
  onQuickEmail?: () => void;
  onOpenAlerts?: () => void;
  onOpenCommercial?: () => void;
  onOpenOperations?: () => void;
  onOpenStrategic?: () => void;
};

const toneByRelationship: Record<ClientRelationship, "neutral" | "success" | "warning"> = {
  active: "success",
  inactive: "neutral",
  paused: "warning",
};

const parseDate = (value: string) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isOlderThanDays = (value: string, days: number) => {
  const parsed = parseDate(value);
  if (!parsed) return false;
  const diffDays = (Date.now() - parsed.getTime()) / (1000 * 60 * 60 * 24);
  return diffDays > days;
};

const isWithinNextDays = (value: string, days: number) => {
  const parsed = parseDate(value);
  if (!parsed) return false;
  const diffDays = (parsed.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
  return diffDays >= 0 && diffDays <= days;
};

const daysSince = (value: string) => {
  const parsed = parseDate(value);
  if (!parsed) return null;
  return Math.max(0, Math.floor((Date.now() - parsed.getTime()) / (1000 * 60 * 60 * 24)));
};

function ActionNeededBadge({ count, items }: { count: number; items: { label: string; onClick?: () => void }[] }) {
  return (
    <Dropdown
      label={`${count} actions`}
      align="right"
      items={items.slice(0, 3).map((item) => ({ label: item.label, onClick: item.onClick }))}
    />
  );
}

function RadarWidget({
  commercialTone,
  operationsTone,
  globalTone,
  score10,
  grade,
  tooltip,
  onClick,
  label,
  sublabel,
  extraLabel,
}: {
  commercialTone: string;
  operationsTone: string;
  globalTone: string;
  score10?: number;
  grade?: string;
  tooltip?: string;
  onClick?: () => void;
  label?: string;
  sublabel?: string;
  extraLabel?: string;
}) {
  return (
    <div className="relative flex flex-col items-center gap-2 group">
      <button
        type="button"
        onClick={onClick}
        aria-label="Ouvrir le détail stratégique"
        className="flex flex-col items-center gap-2"
      >
        <div className="client-avatar-ring transition duration-200 ease-out hover:scale-[1.02]">
          <span className={cn("avatar-half avatar-half-left", commercialTone)} />
          <span className={cn("avatar-half avatar-half-right", operationsTone)} />
          <span className={cn("avatar-inner", globalTone)}>
            <div className="flex flex-col items-center text-primary-foreground">
              <span className="text-lg font-semibold">{score10 ? score10.toFixed(1) : "—"}</span>
              <span className="text-xs opacity-90">{grade ?? "—"}</span>
            </div>
          </span>
        </div>
      </button>
      <Tooltip label="Radar" content="Commercial / Opérations / Global" />
      {label ? <div className="text-xs text-muted">{label}</div> : null}
      {sublabel ? <div className="text-xs text-muted">{sublabel}</div> : null}
      {extraLabel ? <div className="text-xs text-muted">{extraLabel}</div> : null}
      {tooltip ? (
        <div className="pointer-events-none absolute top-full z-10 mt-2 w-48 rounded-md border border-border bg-surface px-2 py-1 text-xs text-text shadow-sm opacity-0 transition duration-150 ease-out group-hover:opacity-100">
          {tooltip.split("\n").map((line, index) => (
            <div key={`${line}-${index}`}>{line}</div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function ClientStrategicHeader({
  client,
  commercial,
  operations,
  finance,
  actionsNeeded,
  onNewQuote,
  onOpenActions,
  onQuickCall,
  onQuickEmail,
  onOpenAlerts,
  onOpenCommercial,
  onOpenOperations,
  onOpenStrategic,
}: ClientStrategicHeaderProps) {
  const commercialCounts = useMemo(
    () =>
      commercial.quotes.reduce(
        (acc, item) => ({ ...acc, [item.status]: (acc[item.status] ?? 0) + item.count }),
        {} as Record<QuoteStatus, number>
      ),
    [commercial.quotes]
  );
  const opsCounts = useMemo(
    () =>
      operations.items.reduce(
        (acc, item) => ({ ...acc, [item.status]: (acc[item.status] ?? 0) + item.count }),
        {} as Record<OperationStatus, number>
      ),
    [operations.items]
  );
  const financeTotals = useMemo(
    () =>
      finance.invoices.reduce(
        (acc, item) => ({ ...acc, [item.state]: (acc[item.state] ?? 0) + item.amount }),
        {} as Record<InvoiceState, number>
      ),
    [finance.invoices]
  );

  const financeRisk = (financeTotals.overdue ?? 0) > 0;
  const opsBlocked = (opsCounts.blocked ?? 0) > 0;
  const globalState =
    client.relationship === "inactive"
      ? "inactive"
      : financeRisk || opsBlocked
      ? "risk"
      : client.score10 && client.score10 >= 8
      ? "good"
      : client.score10 && client.score10 >= 6
      ? "neutral"
      : "watch";

  const commercialSummary =
    commercial.interest === "none"
      ? "Refusé / froid"
      : commercialCounts.accepted
      ? "Achat actif"
      : commercialCounts.sent
      ? "En discussion avancée"
      : commercial.interest === "high"
      ? "Intérêt élevé – aucune soumission"
      : commercialCounts.draft
      ? "À relancer"
      : commercial.interest === "later"
      ? "Plus tard"
      : "Inactif";

  const operationsSummary = opsBlocked
    ? "Bloqué"
    : opsCounts.waiting_deposit
    ? "Attente dépôt"
    : opsCounts.delivering || opsCounts.ready_to_deliver
    ? "En livraison"
    : opsCounts.in_production
    ? "En production"
    : "Aucun service actif";

  const financeSummary = financeRisk
    ? "Risque"
    : financeTotals.sent
    ? "Paiement attendu"
    : financeTotals.planned
    ? "Factures planifiées"
    : "Clear";

  const commercialTone =
    commercialSummary === "Achat actif"
      ? "text-success"
      : commercialSummary === "En discussion avancée"
      ? "text-info"
      : commercialSummary.startsWith("Intérêt")
      ? "text-info"
      : commercialSummary === "À relancer"
      ? "text-warning"
      : commercialSummary === "Refusé / froid"
      ? "text-danger"
      : "text-muted";
  const operationsTone =
    operationsSummary === "Bloqué"
      ? "text-danger"
      : operationsSummary === "Attente dépôt"
      ? "text-warning"
      : operationsSummary === "En livraison"
      ? "text-warning"
      : operationsSummary === "En production"
      ? "text-info"
      : "text-muted";
  const globalTone =
    globalState === "risk" ? "bg-danger" : globalState === "good" ? "bg-success" : globalState === "watch" ? "bg-warning" : "bg-muted";

  const hasDeposit = (finance.depositExpected ?? 0) > 0 || (opsCounts.waiting_deposit ?? 0) > 0;
  const commercialSteps = [
    { label: "Intérêt", tooltip: "Intérêt détecté" },
    { label: "Soumission envoyée", tooltip: "Soumission envoyée au client" },
    { label: "Acceptée", tooltip: "Soumission acceptée" },
    { label: "Dépôt reçu", tooltip: "Dépôt confirmé" },
    { label: "Production", tooltip: "Travaux en production" },
    { label: "Livraison", tooltip: "Livraison en cours" },
    { label: "Terminé", tooltip: "Mission terminée" },
  ];
  const commercialStepIndex =
    opsCounts.delivering || opsCounts.ready_to_deliver
      ? 5
      : opsCounts.in_production
      ? 4
      : hasDeposit
      ? 3
      : commercialCounts.accepted
      ? 2
      : commercialCounts.sent
      ? 1
      : commercial.interest !== "none"
      ? 0
      : 0;

  const operationsSteps = [
    { label: "Attente dépôt", tooltip: "Dépôt requis avant démarrage" },
    { label: "Production", tooltip: "En cours de production" },
    { label: "Livraison", tooltip: "Livraison en cours" },
    { label: "Bloqué", tooltip: "Blocage opérationnel" },
    { label: "Terminé", tooltip: "Opérations terminées" },
  ];
  const operationsStepIndex = opsBlocked
    ? 3
    : opsCounts.delivering || opsCounts.ready_to_deliver
    ? 2
    : opsCounts.in_production
    ? 1
    : opsCounts.waiting_deposit || (finance.depositExpected ?? 0) > 0
    ? 0
    : opsCounts.done
    ? 4
    : 0;

  const lastSentOld = commercial.lastSentAt ? isOlderThanDays(commercial.lastSentAt, 7) : false;
  const lastSentDays = commercial.lastSentAt ? daysSince(commercial.lastSentAt) : null;
  const actionRequired = hasDeposit || lastSentOld || (commercialCounts.draft ?? 0) > 0;
  const opsActiveCount =
    (opsCounts.in_production ?? 0) + (opsCounts.delivering ?? 0) + (opsCounts.ready_to_deliver ?? 0);
  const opsLoadClass =
    opsActiveCount >= 5
      ? "w-full"
      : opsActiveCount >= 4
      ? "w-3/4"
      : opsActiveCount >= 2
      ? "w-1/2"
      : opsActiveCount >= 1
      ? "w-1/4"
      : "w-0";
  const nextDue = operations.items.find((item) => item.nextDueDate)?.nextDueDate;
  const dueSoon = nextDue ? isWithinNextDays(nextDue, 7) : false;
  const scoreTooltip = client.scoreReasons?.length
    ? client.scoreReasons.join("\n")
    : "Intérêt élevé +2\nPaiement à temps +2\nXLHub actif +1\nHistorique positif +2";
  const commercialDetail = hasDeposit
    ? `Actuellement : dépôt en attente${lastSentDays !== null ? ` depuis ${lastSentDays} jours` : ""}`
    : commercialCounts.sent
    ? `Actuellement : soumission envoyée${lastSentDays !== null ? ` depuis ${lastSentDays} jours` : ""}`
    : commercialCounts.draft
    ? "Actuellement : brouillon non envoyé"
    : commercial.interest !== "none"
    ? "Actuellement : intérêt détecté"
    : "Aucune activité commerciale";
  const operationsDetail = opsBlocked
    ? "Actuellement : blocage opérationnel"
    : nextDue
    ? `Actuellement : livraison en cours – échéance ${nextDue}`
    : opsCounts.in_production
    ? "Actuellement : production en cours"
    : "Aucune opération active";
  const commercialImpossible = client.relationship === "inactive" || commercial.interest === "none";
  const operationsImpossible = client.relationship === "inactive";
  const getStepState = (index: number, currentIndex: number, impossible: boolean, blockedIndex?: number) => {
    if (blockedIndex !== undefined && index === blockedIndex) return "blocked";
    if (impossible && index > currentIndex) return "impossible";
    if (index < currentIndex) return "past";
    if (index === currentIndex) return "current";
    return "future";
  };

  const dominantStatus = opsBlocked
    ? "Statut dominant : blocage opérationnel"
    : hasDeposit
    ? "Statut dominant : dépôt requis avant démarrage"
    : opsCounts.delivering || opsCounts.ready_to_deliver
    ? "Statut dominant : livraison en cours"
    : opsCounts.in_production
    ? "Statut dominant : production en cours"
    : commercialCounts.sent
    ? "Statut dominant : soumission en discussion"
    : "Statut dominant : activité normale";

  const radarLabel =
    globalState === "risk"
      ? "Client sous tension"
      : globalState === "good"
      ? "Client stable"
      : globalState === "watch"
      ? "Client à surveiller"
      : "Client inactif";
  const radarRisk =
    globalState === "risk"
      ? "Risque : élevé"
      : globalState === "watch"
      ? "Risque : modéré"
      : globalState === "good"
      ? "Risque : faible"
      : "Risque : neutre";
  const radarPressure =
    opsBlocked || dueSoon ? "Pression opérationnelle : élevée" : opsActiveCount >= 3 ? "Pression opérationnelle : moyenne" : "Pression opérationnelle : faible";

  const commercialTitle = actionRequired
    ? "Commercial : Relance requise"
    : commercialSummary === "Achat actif"
    ? "Commercial : Pipeline engagé"
    : commercialCounts.sent
    ? "Commercial : Opportunité chaude"
    : "Commercial : À surveiller";
  const operationsTitle = opsBlocked
    ? "Opérationnel : Bloqué"
    : dueSoon
    ? "Opérationnel : Échéance proche"
    : opsCounts.in_production
    ? "Opérationnel : Production en cours"
    : opsCounts.delivering
    ? "Opérationnel : Livraison maîtrisée"
    : "Opérationnel : Normal";

  const opsPressureLabel = opsActiveCount >= 5 ? "Charge opérationnelle élevée" : opsActiveCount >= 3 ? "Charge opérationnelle moyenne" : "Charge opérationnelle maîtrisée";
  const opsPressureClass = opsActiveCount >= 5 ? "bg-danger" : opsActiveCount >= 3 ? "bg-warning" : "bg-success";
  const opsPressureWidth = opsActiveCount >= 5 ? "w-full" : opsActiveCount >= 3 ? "w-3/4" : opsActiveCount >= 2 ? "w-1/2" : opsActiveCount >= 1 ? "w-1/4" : "w-0";

  const strategicSummary = opsCounts.delivering || opsCounts.ready_to_deliver
    ? "Livraison en cours – paiement final attendu – aucune tension relationnelle"
    : hasDeposit
    ? "Dépôt attendu – démarrage en attente – relation stable"
    : opsBlocked
    ? "Blocage opérationnel – action requise – relation sous tension"
    : "Statut stratégique : stable";

  return (
    <div className="rounded-md border border-border bg-surface-subtle shadow-sm">
      <div className="sticky top-0 z-10 border-b border-border bg-surface px-6 py-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-title text-text font-semibold">{client.name}</h1>
              <Badge label={client.relationship === "active" ? "Actif" : client.relationship === "paused" ? "Pause" : "Inactif"} tone={toneByRelationship[client.relationship]} />
              {client.xlHubActive ? <Badge label="XLHub actif" tone="neutral" /> : null}
              {client.score10 ? <Badge label={`Score ${client.grade ?? "B"} • ${client.score10}/10`} tone="neutral" /> : null}
            </div>
            <div className="mt-1 text-sm text-muted">
              {[client.region, client.ambassadorName, client.sinceYear ? `Client depuis ${client.sinceYear}` : null]
                .filter(Boolean)
                .join(" • ")}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button label="Nouvelle soumission" intent="primary" size="sm" onClick={onNewQuote} />
            <Dropdown label="Actions" align="right" items={[{ label: "Voir dossier", onClick: onOpenActions }]} />
            <IconButton icon={<Phone size={16} strokeWidth={1.5} />} ariaLabel="Appeler" intent="ghost" size="sm" onClick={onQuickCall} />
            <IconButton icon={<Mail size={16} strokeWidth={1.5} />} ariaLabel="Email" intent="ghost" size="sm" onClick={onQuickEmail} />
            <div className="relative">
              <IconButton icon={<Bell size={16} strokeWidth={1.5} />} ariaLabel="Alertes" intent="ghost" size="sm" onClick={onOpenAlerts} />
              {client.alertsCount ? (
                <span className="absolute -right-1 -top-1 rounded-full bg-danger px-1 text-[10px] text-primary-foreground">
                  {client.alertsCount}
                </span>
              ) : null}
            </div>
            {actionsNeeded ? <ActionNeededBadge count={actionsNeeded.count} items={actionsNeeded.items} /> : null}
          </div>
        </div>
      </div>

      <div className="grid gap-4 px-6 py-4 md:grid-cols-[1fr_auto_1fr]">
        <div
          role="button"
          tabIndex={0}
          onClick={onOpenCommercial}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onOpenCommercial?.();
            }
          }}
          className={cn(
            "rounded-md border border-border px-4 py-4 shadow-md transition duration-150 ease-out hover:-translate-y-0.5 hover:shadow-lg hover:border-primary/30",
            commercialSummary === "Achat actif"
              ? "bg-gradient-to-br from-success/5 to-surface"
              : commercialSummary === "En discussion avancée"
              ? "bg-gradient-to-br from-info/5 to-surface"
              : commercialSummary.startsWith("Intérêt")
              ? "bg-gradient-to-br from-info/5 to-surface"
              : commercialSummary === "Refusé / froid"
              ? "bg-gradient-to-br from-danger/5 to-surface"
              : "bg-surface"
          )}
        >
          <div className="text-xs uppercase tracking-[0.06em] text-text-secondary font-medium">{commercialTitle}</div>
          <div className={cn("mt-2 text-lg font-semibold", commercialTone)}>{commercialSummary}</div>
          <div className="mt-3 space-y-2 text-xs text-muted">
            {actionRequired ? (
              <div className="pipeline-alert">
                <span>
                  Dépôt attendu
                  {lastSentDays !== null ? ` depuis ${lastSentDays} jours` : ""}
                </span>
                <Button label="Relancer client" intent="ghost" size="sm" />
              </div>
            ) : null}
            <div className="pipeline-track pipeline-track--commercial flex flex-wrap items-center gap-2">
              {commercialSteps.map((step, index) => {
                const state = getStepState(index, commercialStepIndex, commercialImpossible);
                return (
                  <div key={step.label} className="group relative">
                    <button
                      type="button"
                      onClick={onOpenCommercial}
                      className={cn(
                        "pipeline-step",
                        state === "past" && "pipeline-step--past",
                        state === "current" && "pipeline-step--current",
                        state === "future" && "pipeline-step--future",
                        state === "impossible" && "pipeline-step--impossible",
                        state === "blocked" && "pipeline-step--blocked"
                      )}
                    >
                      <span className="pipeline-dot" />
                      <span className={cn(state === "current" ? "font-semibold" : "")}>{step.label}</span>
                    </button>
                    <div className="pointer-events-none absolute left-1/2 top-full z-10 mt-2 w-44 -translate-x-1/2 rounded-md border border-border bg-surface px-2 py-1 text-xs text-text shadow-sm opacity-0 transition duration-150 ease-out group-hover:opacity-100">
                      {step.tooltip}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="text-xs text-muted">{commercialDetail}</div>
          </div>
        </div>
        <RadarWidget
          commercialTone={commercialTone}
          operationsTone={operationsTone}
          globalTone={globalTone}
          score10={client.score10}
          grade={client.grade}
          tooltip={scoreTooltip}
          onClick={onOpenStrategic}
          label={radarLabel}
          sublabel={radarPressure}
          extraLabel={radarRisk}
        />
        <div
          role="button"
          tabIndex={0}
          onClick={onOpenOperations}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onOpenOperations?.();
            }
          }}
          className={cn(
            "rounded-md border border-border px-4 py-4 shadow-md transition duration-150 ease-out hover:-translate-y-0.5 hover:shadow-lg hover:border-primary/30",
            opsBlocked
              ? "bg-gradient-to-br from-danger/5 to-surface"
              : opsCounts.waiting_deposit
              ? "bg-gradient-to-br from-warning/5 to-surface"
              : opsCounts.in_production
              ? "bg-gradient-to-br from-info/5 to-surface"
              : "bg-surface"
          )}
        >
          <div className="text-xs uppercase tracking-[0.06em] text-text-secondary font-medium">{operationsTitle}</div>
          <div className={cn("mt-2 text-lg font-semibold", operationsTone)}>{operationsSummary}</div>
          <div className="mt-3 space-y-2 text-xs text-muted">
            <div className="pipeline-track pipeline-track--ops flex flex-wrap items-center gap-2">
              {operationsSteps.map((step, index) => {
                const blockedIndex = opsBlocked ? 3 : undefined;
                const state = getStepState(index, operationsStepIndex, operationsImpossible, blockedIndex);
                return (
                  <div key={step.label} className="group relative">
                    <button
                      type="button"
                      onClick={onOpenOperations}
                      className={cn(
                        "pipeline-step",
                        state === "past" && "pipeline-step--past-ops",
                        state === "current" && "pipeline-step--current",
                        state === "future" && "pipeline-step--future",
                        state === "impossible" && "pipeline-step--impossible",
                        state === "blocked" && "pipeline-step--blocked"
                      )}
                    >
                      <span className="pipeline-dot" />
                      <span className={cn(state === "current" ? "font-semibold" : "")}>{step.label}</span>
                    </button>
                    <div className="pointer-events-none absolute left-1/2 top-full z-10 mt-2 w-44 -translate-x-1/2 rounded-md border border-border bg-surface px-2 py-1 text-xs text-text shadow-sm opacity-0 transition duration-150 ease-out group-hover:opacity-100">
                      {step.tooltip}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="text-xs text-muted">{operationsDetail}</div>
          </div>
          <div className="mt-3">
            <div className="text-xs text-muted">{opsPressureLabel}</div>
            <div className="mt-2 h-1.5 w-full rounded-full bg-border">
              <div className={cn("h-1.5 rounded-full", opsPressureClass, opsPressureWidth)} />
            </div>
            {dueSoon ? <div className="mt-2 text-xs text-danger">Échéance proche</div> : null}
          </div>
        </div>
      </div>

      <div className="px-6 pb-2 text-center text-sm text-text-secondary">{dominantStatus}</div>
      <div className="px-6 pb-4 text-center text-sm font-medium text-text">{strategicSummary}</div>

      <div className="px-6 pb-6" />
    </div>
  );
}
